/**
 * Correlation Context Management
 *
 * This module handles correlation context detection, generation, and management
 * for end-to-end request tracking across frontend and backend systems.
 *
 * Separated from errorReporter to maintain clean separation of concerns:
 * - correlationContext.ts: Context detection and management
 * - errorReporter.ts: Error reporting with correlation tags
 */
import { getDefaultStore } from "jotai";
import { impersonationAtom } from "~/atoms/impersonation";

/**
 * Correlation context for tracking requests across systems
 */
export interface CorrelationContext {
  correlationID: string;
  appointmentID?: string;
  userID?: string;
  timestamp: string;
  impersonatorID?: string;
  impersonationSessionID?: string;
}

/**
 * Global user context for correlation detection
 */
interface GlobalUserContext {
  id: string;
}

// Global state for user context
let globalUserContext: GlobalUserContext | null = null;

/**
 * Sets the global user context for correlation detection.
 * This should be called when the user logs in or user data is loaded.
 *
 * @param userContext - User context with ID and optional name
 */
export const setGlobalUserContextForCorrelation = (
  userContext: GlobalUserContext | null,
): void => {
  globalUserContext = userContext;
};

/**
 * Gets the current global user context.
 *
 * @returns Current user context or null if not set
 */
export const getGlobalUserContext = (): GlobalUserContext | null => {
  return globalUserContext;
};

/**
 * Detects appointment ID from the current URL path.
 * Supports patterns like:
 * - /appointments/:appointmentId
 * - /appointments/:appointmentId/edit
 * - /appointments/:appointmentId/recap
 *
 * @returns Appointment ID if detected, null otherwise
 */
export const detectAppointmentIdFromURL = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const path = window.location.pathname;
  const appointmentMatch = path.match(/\/appointments\/([^/?#]+)/);

  if (appointmentMatch && appointmentMatch[1]) {
    const appointmentId = appointmentMatch[1];
    // Filter out non-appointment routes
    if (
      ["new", "create", "list", "search"].includes(appointmentId.toLowerCase())
    ) {
      return null;
    }
    return appointmentId;
  }

  return null;
};

/**
 * Automatically detects and generates correlation context from the current environment.
 *
 * This function:
 * 1. Generates a unique correlation ID
 * 2. Auto-detects appointment ID from URL (can be overridden)
 * 3. Includes user context if available
 * 4. Adds current page context
 * 5. Includes timestamp
 *
 * @returns Complete correlation context or null if no context can be determined
 */
export const getAutoCorrelationContext = (): CorrelationContext | null => {
  const correlationID = crypto.randomUUID();
  const timestamp = new Date().toISOString();

  // Detect appointment ID
  const appointmentID = detectAppointmentIdFromURL();

  // Get user context
  const userContext = getGlobalUserContext();

  // Read impersonation state from Jotai store
  const impersonation = getDefaultStore().get(impersonationAtom);

  // Only return context if we have some meaningful data
  if (appointmentID || userContext) {
    return {
      correlationID,
      appointmentID: appointmentID || undefined,
      userID: userContext?.id,
      timestamp,
      ...(impersonation.isActive && {
        impersonatorID: impersonation.adminCognitoSub ?? undefined,
        impersonationSessionID: impersonation.sessionId ?? undefined,
      }),
    };
  }

  return null;
};

/**
 * Creates correlation tags from correlation context for error reporting.
 * This is a pure function that takes correlation context and returns tags.
 *
 * @param correlationContext - Correlation context to convert to tags
 * @returns Tags object with correlation fields
 */
export const createCorrelationTags = (
  correlationContext: CorrelationContext,
): Record<string, string> => {
  const tags: Record<string, string> = {
    correlationID: correlationContext.correlationID,
  };

  if (correlationContext.appointmentID) {
    tags.appointmentID = correlationContext.appointmentID;
  }

  if (correlationContext.userID) {
    tags.userID = correlationContext.userID;
  }

  if (correlationContext.impersonatorID) {
    tags.impersonatorID = correlationContext.impersonatorID;
  }

  if (correlationContext.impersonationSessionID) {
    tags.impersonationSessionID = correlationContext.impersonationSessionID;
  }

  return tags;
};
